import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as math from 'mathjs';
import { Box } from '@chakra-ui/react';
import { SheetShapeDeepFragment } from '../../../gql/graphql';
import { toMultiPolygon2 } from '../../../domain/geometry/algorithms/util/type-mapping';
import {
  LineSegment2,
  MultiPolygon2,
  Point2,
} from '../../../domain/geometry/geometric-types';
import { SheetPolygon } from './SheetPolygon';
import { SheetEdge } from './SheetEdge';
import { SheetVertex } from './SheetVertex';
import { SheetTranslationFeedbackLine } from './SheetTranslationFeedbackLine';
import { SheetsViewerV2Context } from 'src/components/viewers/SheetsViewerV2/SheetsViewerV2.context';
import { ApiCoordinatesConverter } from 'src/components/viewers/SheetsViewerV2/utils/apiCoordinatesConverter';
import { VIEWER_TOP_OFFSET } from 'src/components/viewers/SheetsViewerV2/SheetsViewerV2.config';

export const MoveOrCopySheetShapes = ({
  shapes,
  onResult,
}: {
  shapes: SheetShapeDeepFragment[];
  onResult: (translationVector: Point2) => void;
}) => {
  const [startPoint, setStartPoint] = useState<Point2 | null>(null);
  const [intermediatePoint, setIntermediatePoint] = useState<Point2 | null>(
    null
  );

  const sheetViewerV2 = useContext(SheetsViewerV2Context);

  const viewerRef = sheetViewerV2?.viewer.elementRef;
  const calibration = sheetViewerV2?.calibration.existing ?? null;

  // Helper function to get point from v2 viewer
  const getPointFromV2Viewer = useCallback(
    (clientX: number, clientY: number): Point2 | null => {
      if (
        !sheetViewerV2 ||
        !sheetViewerV2.viewer.aggregated ||
        !sheetViewerV2.activeSheet?.data
      ) {
        return null;
      }
      const viewer = sheetViewerV2.viewer.aggregated.viewer;
      const converter = new ApiCoordinatesConverter(
        viewer,
        sheetViewerV2.activeSheet.data.viewport
      );
      const result = viewer.impl.clientToWorld(
        clientX,
        clientY - VIEWER_TOP_OFFSET
      );
      if (result && result.point) {
        const api = converter.convertToApiPoint(result.point);
        return [api.x, api.y];
      }
      return null;
    },
    [sheetViewerV2]
  );

  // Get the point in the DOM coordinate system
  const getPointInDomCoordinateSystem = useCallback(
    (pointInPdf: Point2): Point2 => {
      if (
        !sheetViewerV2 ||
        !sheetViewerV2.viewer.aggregated ||
        !sheetViewerV2.activeSheet?.data
      )
        throw new Error('Viewer not ready');

      const converter = new ApiCoordinatesConverter(
        sheetViewerV2.viewer.aggregated.viewer,
        sheetViewerV2.activeSheet.data.viewport
      );
      const world = converter.convertToAutodeskPoint({
        x: pointInPdf[0],
        y: pointInPdf[1],
      });
      const screen =
        sheetViewerV2.viewer.aggregated.viewer.worldToClient(world);
      return [screen.x, screen.y] as Point2;
    },
    [sheetViewerV2]
  );

  // Handle length submission from the feedback line input
  const handleLengthSubmit = useCallback(
    (newLengthInPdfUnits: number) => {
      if (!startPoint || !intermediatePoint) {
        return;
      }

      // Calculate direction vector from start to intermediate point
      const direction: Point2 = [
        intermediatePoint[0] - startPoint[0],
        intermediatePoint[1] - startPoint[1],
      ];
      const currentLength = math.norm(direction) as number;

      if (currentLength === 0) {
        return;
      }

      // Normalize the direction
      const normalizedDirection: Point2 = [
        direction[0] / currentLength,
        direction[1] / currentLength,
      ];

      // Calculate the translation vector with the new length
      const translationVector: Point2 = [
        normalizedDirection[0] * newLengthInPdfUnits,
        normalizedDirection[1] * newLengthInPdfUnits,
      ];

      onResult(translationVector);
      setStartPoint(null);
      setIntermediatePoint(null);
    },
    [startPoint, intermediatePoint, onResult]
  );

  // Helper function to handle point selection
  const handlePointSelection = useCallback(
    (clientX: number, clientY: number) => {
      const newPoint = getPointFromV2Viewer(clientX, clientY);

      if (!newPoint) {
        return null;
      }

      if (!startPoint) {
        setStartPoint(newPoint);
        return null;
      } else {
        const translationVector: Point2 = [
          newPoint[0] - startPoint[0],
          newPoint[1] - startPoint[1],
        ];
        onResult(translationVector);
        setStartPoint(null);
        return translationVector;
      }
    },
    [getPointFromV2Viewer, startPoint, onResult]
  );

  useEffect(() => {
    const currentViewer = viewerRef?.current;
    if (!currentViewer) {
      return;
    }

    const handlePageClick = (event: MouseEvent) => {
      if (event.button !== 0) return;

      const result = handlePointSelection(event.clientX, event.clientY);
      if (result !== null) {
        event.stopPropagation();
      }
    };

    currentViewer.addEventListener('click', handlePageClick);
    return () => {
      currentViewer.removeEventListener('click', handlePageClick);
    };
  }, [handlePointSelection, viewerRef]);

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      const newPoint = getPointFromV2Viewer(event.clientX, event.clientY);

      if (!newPoint) {
        return;
      }
      setIntermediatePoint(newPoint);
    },
    [getPointFromV2Viewer]
  );

  useEffect(() => {
    const current = viewerRef?.current;
    if (!current) {
      return;
    }
    current.addEventListener('pointermove', handlePointerMove);
    return () => {
      current.removeEventListener('pointermove', handlePointerMove);
    };
  }, [handlePointerMove, viewerRef]);

  const translatedShapes = useMemo(() => {
    if (!startPoint || !intermediatePoint) return null;

    const translation = {
      x: intermediatePoint[0] - startPoint[0],
      y: intermediatePoint[1] - startPoint[1],
    };

    return (
      <Box
        width={'100%'}
        height={'100%'}
        position={'absolute'}
        top={0}
        left={0}
        zIndex={1000}
        pointerEvents={'none'}
      >
        {shapes.map((shape) => {
          if ('sheetShapePolygon' in shape && shape.sheetShapePolygon) {
            return IntermediatePolygonShape(
              shape,
              translation,
              getPointInDomCoordinateSystem
            );
          } else if ('sheetShapeLine' in shape && shape.sheetShapeLine) {
            return IntermediateLineShape(
              shape,
              translation,
              getPointInDomCoordinateSystem
            );
          } else if ('sheetShapePoint' in shape && shape.sheetShapePoint) {
            return IntermediatePointShape(
              shape,
              translation,
              getPointInDomCoordinateSystem
            );
          } else if ('sheetShapeLegend' in shape && shape.sheetShapeLegend) {
            return IntermediateLegendShape(
              shape,
              translation,
              getPointInDomCoordinateSystem
            );
          }
        })}
      </Box>
    );
  }, [startPoint, intermediatePoint, shapes, getPointInDomCoordinateSystem]);

  // Don't render if V2 viewer is not available
  if (!sheetViewerV2) {
    return null;
  }

  return (
    <>
      {translatedShapes}
      {/* Show the dashed feedback line with length label when reference point is set */}
      {startPoint && intermediatePoint && (
        <SheetTranslationFeedbackLine
          startPoint={startPoint}
          endPoint={intermediatePoint}
          getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
          calibration={calibration}
          onLengthSubmit={handleLengthSubmit}
        />
      )}
    </>
  );
};

const IntermediatePolygonShape = (
  shape: SheetShapeDeepFragment,
  translation: { x: number; y: number },
  getPointInDomCoordinateSystem: (pointInPdf: Point2) => Point2
) => {
  const clonedShape: SheetShapeDeepFragment = JSON.parse(JSON.stringify(shape));
  clonedShape.sheetShapePolygon?.multipolygon.polygons.forEach((polygon) => {
    polygon.exterior.points.forEach((point) => {
      point.x += translation.x;
      point.y += translation.y;
    });
    polygon.interiors.forEach((interior) => {
      interior.points.forEach((point) => {
        point.x += translation.x;
        point.y += translation.y;
      });
    });
  });

  return clonedShape.sheetShapePolygon?.multipolygon ? (
    <SheetPolygon
      key={shape.id}
      multipolygon={toMultiPolygon2(
        clonedShape.sheetShapePolygon?.multipolygon
      )}
      getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
      fill={'orange.300'}
    />
  ) : null;
};

const IntermediateLineShape = (
  shape: SheetShapeDeepFragment,
  translation: { x: number; y: number },
  getPointInDomCoordinateSystem: (pointInPdf: Point2) => Point2
) => {
  const clonedShape: SheetShapeDeepFragment = JSON.parse(JSON.stringify(shape));
  clonedShape.sheetShapeLine?.points.forEach((point) => {
    point.x += translation.x;
    point.y += translation.y;
  });

  const edges: LineSegment2[] = [];

  if (clonedShape.sheetShapeLine?.points) {
    for (let i = 0; i < clonedShape.sheetShapeLine.points.length - 1; i++) {
      edges.push([
        [
          clonedShape.sheetShapeLine.points[i].x,
          clonedShape.sheetShapeLine.points[i].y,
        ],
        [
          clonedShape.sheetShapeLine.points[i + 1].x,
          clonedShape.sheetShapeLine.points[i + 1].y,
        ],
      ]);
    }
  }

  return edges.map((edge, index) => (
    <SheetEdge
      key={shape.id + index}
      segment={edge}
      cursor={'pointer'}
      getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
      backgroundColor={'orange.300'}
    />
  ));
};

const IntermediatePointShape = (
  shape: SheetShapeDeepFragment,
  translation: { x: number; y: number },
  getPointInDomCoordinateSystem: (pointInPdf: Point2) => Point2
) => {
  const clonedShape: SheetShapeDeepFragment = JSON.parse(JSON.stringify(shape));
  if (clonedShape.sheetShapePoint) {
    clonedShape.sheetShapePoint.point.x += translation.x;
    clonedShape.sheetShapePoint.point.y += translation.y;

    return (
      <SheetVertex
        key={shape.id}
        point={[
          clonedShape.sheetShapePoint.point.x,
          clonedShape.sheetShapePoint.point.y,
        ]}
        getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
        theme={'default'}
        backgroundColor={'orange.300'}
        borderColor={'orange.300'}
      />
    );
  }
};

// Legend shape constants for intermediate preview
// These match the viewer coordinate constants in LegendShape.tsx,
// scaled to PDF/API coordinates (scale factor ~71.4 based on ROW_HEIGHT)
const LEGEND_SCALE_FACTOR = 71.4; // Approximate scale from viewer to PDF coords
const LEGEND_ROW_HEIGHT_SCALED = 0.35 * LEGEND_SCALE_FACTOR; // ~25
const LEGEND_PADDING_SCALED = 0.12 * LEGEND_SCALE_FACTOR; // ~8.6
const LEGEND_MIN_WIDTH_SCALED = 2.0 * LEGEND_SCALE_FACTOR; // ~143
const LEGEND_COLOR_SWATCH_SIZE_SCALED = 0.2 * LEGEND_SCALE_FACTOR;
const LEGEND_TEXT_HEIGHT_SCALED = 0.18 * LEGEND_SCALE_FACTOR;
const LEGEND_TEXT_PADDING_SCALED = 0.08 * LEGEND_SCALE_FACTOR;

const IntermediateLegendShape = (
  shape: SheetShapeDeepFragment,
  translation: { x: number; y: number },
  getPointInDomCoordinateSystem: (pointInPdf: Point2) => Point2
) => {
  if (!shape.sheetShapeLegend) {
    return null;
  }

  const position = {
    x: shape.sheetShapeLegend.position.x + translation.x,
    y: shape.sheetShapeLegend.position.y + translation.y,
  };

  const entries = shape.sheetShapeLegend.legendOrder?.orderEntries || [];
  const entriesCount = entries.length || 1;

  // Calculate height based on entries
  const legendHeight =
    entriesCount * LEGEND_ROW_HEIGHT_SCALED + LEGEND_PADDING_SCALED * 2;

  // Calculate width based on longest entry name (same logic as LegendShape)
  const charWidth = LEGEND_TEXT_HEIGHT_SCALED * 0.5;
  const maxNameLength = Math.max(
    ...entries.map((e) => e.name?.length || 0),
    10 // minimum 10 chars
  );
  const textWidth = maxNameLength * charWidth;

  const legendWidth = Math.max(
    LEGEND_MIN_WIDTH_SCALED,
    LEGEND_PADDING_SCALED +
      LEGEND_COLOR_SWATCH_SIZE_SCALED +
      LEGEND_TEXT_PADDING_SCALED +
      textWidth +
      LEGEND_PADDING_SCALED
  );

  // Create a polygon representing the legend bounds
  // Position is top-left corner, legend extends right and down (negative y in PDF coords)
  const topLeft: Point2 = [position.x, position.y];
  const topRight: Point2 = [position.x + legendWidth, position.y];
  const bottomRight: Point2 = [
    position.x + legendWidth,
    position.y - legendHeight,
  ];
  const bottomLeft: Point2 = [position.x, position.y - legendHeight];

  const multipolygon: MultiPolygon2 = {
    polygons: [
      {
        exterior: [topLeft, topRight, bottomRight, bottomLeft, topLeft],
        interiors: [],
      },
    ],
  };

  return (
    <SheetPolygon
      key={shape.id}
      multipolygon={multipolygon}
      getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
      fill={'orange.300'}
    />
  );
};
